import { Children, FC, HTMLProps, isValidElement, ReactElement, useState } from 'react';

import { Button } from '@purpur/library';
import AnimateHeight from 'react-animate-height';

import { useComponentId } from '@telialt/utils/src/hooks';

type RevealingListON = {
    /** show many items should be visible, other are hidden */
    maxVisibleItems?: number;
    showMoreText: string;
    showLessText: string;
    isInitiallyExpanded?: boolean;
};

type RevealingListOFF = {
    maxVisibleItems?: 'all';
    showMoreText?: string;
    showLessText?: string;
    isInitiallyExpanded?: boolean;
};

export type RevealingListProps = RevealingListON | RevealingListOFF;

type Props = RevealingListProps & HTMLProps<HTMLDivElement> & { children: ReactElement<{ hidden: boolean }>[] };

export const RevealingList: FC<Props> = ({
    maxVisibleItems = 'all',
    showMoreText,
    showLessText,
    isInitiallyExpanded = false,
    children,
    ...listProps
}) => {
    const hiddenListId = useComponentId('hidden-list');

    const validChildren = Children.toArray(children).filter((child: ReactElement<{ hidden: boolean }>) =>
        isValidElement(child) ? !child.props.hidden : child,
    );
    const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);

    if (maxVisibleItems === 'all' || Children.count(validChildren) <= maxVisibleItems) {
        return (
            <div role="list" {...listProps}>
                {children}
            </div>
        );
    }

    const visibleChildren = Children.toArray(validChildren).slice(0, maxVisibleItems);
    const hiddenChildren = Children.toArray(validChildren).slice(maxVisibleItems);

    return (
        <div role="list" {...listProps}>
            {visibleChildren}
            <AnimateHeight id={hiddenListId} height={isExpanded ? 'auto' : 0} duration={500}>
                {hiddenChildren}
            </AnimateHeight>
            <Button
                variant="text"
                aria-controls={hiddenListId}
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
            >
                {isExpanded ? showLessText : showMoreText}
            </Button>
        </div>
    );
};
